/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { Fragment, useState, type ReactElement, type ReactNode } from 'react';
import { ChevronRight, Heart, MapPin, Truck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from '@/components/link';
import { SheetClose, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { routes, routeHref } from '@/route-paths';
import type { ShopperProducts } from '@/scapi';
import type { NavCategory, NavFeaturedLink, NavTopCategory } from '@/lib/navigation/types';
import { toNavTreeWithDeferred } from '@/lib/navigation/normalize';
import { useSubCategoryMap } from '@/components/navigation-menu/context';
import { SubnavGroups } from '@/components/navigation/subnav-groups';
import { MobileNavHeader } from '@/components/navigation/mobile-nav-header';
import { keepOpenOnBottomNavInteraction } from '@/components/navigation/bottom-nav-interaction';
import { resolveHeaderContent } from '@/lib/header/header-content';
import HeaderLogo from '@/components/header/header-logo';
/** @sfdc-extension-line SFDC_EXT_STORE_LOCATOR */
import { useStoreLocator } from '@/extensions/store-locator/providers/store-locator';

/** SCO-NAV-05 Small: two columns for an Across group, one for everything else. */
const SMALL_COLUMNS = 2;

function EntryList({
    categories,
    onOpen,
    onNavigate,
}: {
    categories: NavCategory[];
    /** Absent for a category with nothing below it, which then links instead of drilling in. */
    onOpen: (category: NavCategory) => void;
    onNavigate: () => void;
}): ReactElement {
    return (
        <ul className="flex flex-col gap-3">
            {categories.map((category, index) => (
                <Fragment key={category.id}>
                    {index > 0 ? <li aria-hidden className="h-px bg-accent" /> : null}
                    <li>
                        {category.children.length > 0 ? (
                            <button
                                type="button"
                                onClick={() => onOpen(category)}
                                className="flex w-full items-center justify-between text-left font-serif text-h4 font-normal text-primary">
                                {category.name}
                                <ChevronRight className="size-4 shrink-0" aria-hidden />
                            </button>
                        ) : (
                            <Link
                                to={category.url}
                                onClick={onNavigate}
                                className="flex w-full items-center justify-between font-serif text-h4 font-normal text-primary">
                                {category.name}
                            </Link>
                        )}
                    </li>
                </Fragment>
            ))}
        </ul>
    );
}

function FeaturedLinkList({
    links,
    onNavigate,
}: {
    links: NavFeaturedLink[];
    onNavigate: () => void;
}): ReactElement | null {
    if (links.length === 0) return null;

    return (
        <ul className="flex flex-col gap-3">
            {links.map((link) => (
                <li key={link.url}>
                    <Link to={link.url} onClick={onNavigate} className="text-body-s text-foreground">
                        {link.text}
                    </Link>
                </li>
            ))}
        </ul>
    );
}

/** SCO-NAV-02 C. The destinations belong to other tickets; only the block is ours. */
function UtilityLinks({ onNavigate }: { onNavigate: () => void }): ReactElement {
    const { t } = useTranslation('header');
    // @sfdc-extension-block-start SFDC_EXT_STORE_LOCATOR
    // FSD 1.5 A reads `<City>`; the name covers a cookie written before city was carried.
    const selectedStoreCity = useStoreLocator((store) => store.selectedStoreInfo?.city);
    const selectedStoreName = useStoreLocator((store) => store.selectedStoreInfo?.name);
    const storeLocatorIsOpen = useStoreLocator((store) => store.isOpen);
    const openStoreLocator = useStoreLocator((store) => store.open);
    const storeLabel =
        selectedStoreCity?.trim() || selectedStoreName?.trim() || t('navigationStoreLocator', 'Find a Store');
    // @sfdc-extension-block-end SFDC_EXT_STORE_LOCATOR
    const className = 'flex items-center gap-2 text-label-xs text-primary uppercase underline';

    return (
        <div className="flex flex-col gap-5 bg-secondary px-6 py-8">
            {/* @sfdc-extension-block-start SFDC_EXT_STORE_LOCATOR */}
            {/* FSD 1.5 A: opens the Location overlay over this menu, which stays open beneath
                it (Figma 4548:45816). Wrapped so `open()` does not read the event as options. */}
            <button
                type="button"
                aria-haspopup="dialog"
                aria-expanded={storeLocatorIsOpen}
                onClick={() => openStoreLocator()}
                className={className}>
                <MapPin className="size-4 shrink-0" aria-hidden />
                {storeLabel}
            </button>
            {/* @sfdc-extension-block-end SFDC_EXT_STORE_LOCATOR */}
            {/* The guest track-order page is FSD 5.X and has no route yet; the account
                order list is the nearest destination that exists. */}
            <Link to={routeHref(routes.accountOrders)} onClick={onNavigate} className={className}>
                <Truck className="size-4 shrink-0" aria-hidden />
                {t('navigationTrackOrder', 'Track order')}
            </Link>
            <Link to={routeHref(routes.wishlist)} onClick={onNavigate} className={className}>
                <Heart className="size-4 shrink-0" aria-hidden />
                {t('navigationWishlist', 'Wish list')}
            </Link>
        </div>
    );
}

/** The open screens from the top down; an id no longer in the tree ends the path there. */
function resolvePath(tree: NavTopCategory[], ids: readonly string[]): NavCategory[] {
    const path: NavCategory[] = [];
    let level: readonly NavCategory[] = tree;
    for (const id of ids) {
        const node = level.find((category) => category.id === id);
        if (!node) break;
        path.push(node);
        level = node.children;
    }
    return path;
}

/**
 * The Small navigation overlay (FSD 1.2). A featured level 1 drills three screens deep
 * — top level, its level 2 drawers, then their groups — while a regular one only has
 * two, because its tree stops a level higher (SCO-NAV-04).
 *
 * The sticky bottom bar that normally opens this is SCO-NAV-01 and lives in another
 * ticket; the header renders a temporary trigger in its place.
 */
export function MobileNav({
    categories,
    onNavigate,
    renderContentSlot,
    initialPathIds = [],
}: {
    categories: ShopperProducts.schemas['Category'][];
    onNavigate: () => void;
    renderContentSlot?: (category: NavCategory) => ReactNode;
    /** Builder preview only: the level 1 (and drawer) the sheet opens drilled into. */
    initialPathIds?: string[];
}): ReactElement {
    const { t } = useTranslation('header');
    const subCategories = useSubCategoryMap();
    const tree = categories.map((category) => toNavTreeWithDeferred(category, subCategories));
    // The same wordmark the header draws, resolved here rather than threaded through
    // Header, which renders this subtree as opaque children. The overlay shows the mark
    // alone — the tagline belongs to the page header, not to this 64px row.
    const logo = <HeaderLogo content={{ ...resolveHeaderContent(t).logo, taglineSrc: undefined }} className="h-8" />;
    // Ids, resolved against the live tree each render, so groups that arrive later still show.
    const [pathIds, setPathIds] = useState<string[]>(initialPathIds);
    const path = resolvePath(tree, pathIds);

    const current = path.at(-1);
    const top = path[0] as NavTopCategory | undefined;

    const open = (category: NavCategory) => setPathIds((previous) => [...previous, category.id]);
    const back = () => setPathIds((previous) => previous.slice(0, -1));

    // A featured level 1 keeps its level 2 as a drawer list; a regular one goes straight
    // to the groups, since its level 2 *is* the grouping heading (SCO-NAV-04).
    const showsGroups = current !== undefined && (path.length > 1 || top?.featured === false);

    return (
        <SheetContent
            side="left"
            // Full-screen at every Small width, per SCO-NAV-03; the built-in close is
            // replaced by the one in the navigation header, which the design places lower.
            // Ends above the bottom bar, which the design keeps visible (Figma Menu Frame).
            className="max-w-none gap-5 bottom-auto h-[calc(100dvh-var(--bottom-nav-height))] lg:bottom-0 lg:h-full [&>button:last-of-type]:hidden"
            onInteractOutside={keepOpenOnBottomNavInteraction}
            aria-label={t('mobileNavigation', 'Mobile navigation menu')}
            // The list is the content; a description would only repeat the label.
            aria-describedby={undefined}>
            <SheetTitle className="sr-only">{t('navigationTitle', 'Menu')}</SheetTitle>

            {/* Keyed so each screen cross-fades in and starts scrolled to the top, rather
                than swapping in place — the Figma prototype dissolves between levels.
                Its timing is a prototype transition, which the API does not expose, so the
                duration follows the overlay's own rather than being read off the design. */}
            <div
                key={current?.id ?? 'root'}
                data-slot="mobile-nav-body"
                className="flex min-h-0 flex-1 animate-in flex-col gap-3 overflow-y-auto p-6 fade-in duration-200 motion-reduce:animate-none">
                {/* SCO-486: the row lives inside the scroller, so it scrolls away with the content
                    rather than staying pinned above it (SCO-481 on the Care drawer). `-mx-6 -mt-6`
                    cancel this column's own `p-6`, leaving the row's `px-4 pt-6` as it was, and
                    `mb-8` (32px) plus the column's `gap-3` (12px) reproduces the 44px the row-to-
                    content gap already had outside it (`gap-5` 20px + `p-6` 24px). */}
                <div className="-mx-6 -mt-6 mb-8">
                    <MobileNavHeader title={current?.name} logo={logo} onBack={current ? back : undefined} />
                </div>

                {current === undefined ? (
                    <EntryList categories={tree} onOpen={open} onNavigate={onNavigate} />
                ) : showsGroups ? (
                    <SubnavGroups
                        groups={current.children}
                        columns={SMALL_COLUMNS}
                        packStandardGroups={false}
                        onNavigate={onNavigate}
                    />
                ) : (
                    /* SCO-486: this pair carries its own 32px gap, read from Figma node 4548:45764
                       ("Menu Frame"), container I4548:45764;34888:15067;34888:13939, which sets
                       `gap-[var(--p-8,32px)]` on the container rather than on either list. It cannot
                       ride the column's `gap-3`: that gap is shared with the groups screen, where
                       SCO-485 composes 12px plus a 36px tile margin into 48px. One property, not two. */
                    <div className="flex flex-col gap-8">
                        <FeaturedLinkList links={top?.featuredLinks ?? []} onNavigate={onNavigate} />
                        <EntryList categories={current.children} onOpen={open} onNavigate={onNavigate} />
                    </div>
                )}

                {/* The open level: the L2 on a featured category (SCO-NAV-03 D), the L1 on a
                    regular one (SCO-NAV-04). */}
                {showsGroups ? renderContentSlot?.(current) : null}
            </div>

            {current === undefined ? <UtilityLinks onNavigate={onNavigate} /> : null}

            <SheetClose className="sr-only">{t('closeMenu', 'Close menu')}</SheetClose>
        </SheetContent>
    );
}
